import { DateTime } from 'luxon';

// ISO 4217 codes we accept for Stripe checkout. Must be enabled on the
// tenant's Stripe account. Keep in sync with the markets we operate in.
export const SUPPORTED_CURRENCIES = new Set<string>([
  'EUR',
  'SEK',
  'GBP',
  'PLN',
  'CZK',
  'CNY',
  'DKK',
  'NOK',
  'USD',
  'JPY',
]);

// Stripe zero-decimal currencies: amounts are the whole-unit value, not ×100.
export const ZERO_DECIMAL_CURRENCIES = new Set<string>([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

// Approximate Stripe minimum charge per currency, in minor units. Values are
// conservative and should be confirmed against Stripe's current minimums.
const MINIMUM_CHARGE_MINOR: Record<string, number> = {
  EUR: 50,
  GBP: 30,
  USD: 50,
  SEK: 300,
  DKK: 250,
  NOK: 300,
  PLN: 200,
  CZK: 1500,
  CNY: 400,
  JPY: 50,
};

const DEFAULT_MINIMUM_CHARGE_MINOR = 50;

export const MAX_QUANTITY = 5;

const NUMERIC_PRICE = /^\d+(\.\d+)?$/;

/**
 * Parses a Contentful `salesPrice` string into a number.
 * Accepts plain numeric strings only (e.g. '199', '199.00', '0', ' 199 ').
 * Rejects empty, negative, locale-comma ('1,99'), multi-dot, and non-numeric
 * values by returning null. '0' returns 0 (handled as "free" by isPurchasable).
 */
export const parseSalesPrice = (value: unknown): number | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || !NUMERIC_PRICE.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export const isCurrencySupported = (code: unknown): boolean =>
  typeof code === 'string' && SUPPORTED_CURRENCIES.has(code.toUpperCase());

/**
 * Converts a major-unit amount to Stripe minor units for the given currency.
 * Zero-decimal currencies are not multiplied. Uses Math.round to avoid float
 * drift (e.g. 19.99 * 100). Returns null for unsupported currencies.
 */
export const toMinorUnits = (
  amount: number,
  currencyCode: string,
): number | null => {
  if (!Number.isFinite(amount) || amount < 0) return null;
  if (!isCurrencySupported(currencyCode)) return null;
  const code = currencyCode.toUpperCase();
  const factor = ZERO_DECIMAL_CURRENCIES.has(code) ? 1 : 100;
  // toPrecision(12) removes binary float drift (e.g. 2.675*100 = 267.4999…)
  // before rounding, so half-cent values round in the expected direction.
  return Math.round(Number((amount * factor).toPrecision(12)));
};

export const getMinimumChargeMinor = (currencyCode: string): number => {
  const code = currencyCode.toUpperCase();
  return MINIMUM_CHARGE_MINOR[code] ?? DEFAULT_MINIMUM_CHARGE_MINOR;
};

export const meetsMinimumCharge = (
  minorUnits: number,
  currencyCode: string,
): boolean => minorUnits >= getMinimumChargeMinor(currencyCode);

export interface PurchasableOffer {
  salesPrice?: string | null;
  offerValidTill?: string | null;
}

/**
 * Whether an offer can be purchased at `now`. False when the price is
 * missing/non-numeric/free (0), or when `offerValidTill` has passed using the
 * same day-boundary rule as the offers list. `now` is injected for testability.
 * A missing `offerValidTill` means no expiry; an unparseable one is treated as
 * expired (fail closed).
 */
export const isPurchasable = (
  offer: PurchasableOffer | null | undefined,
  now: DateTime,
): boolean => {
  if (!offer) return false;
  const price = parseSalesPrice(offer.salesPrice);
  if (price === null || price <= 0) return false;

  if (!offer.offerValidTill) return true;
  const till = DateTime.fromISO(offer.offerValidTill);
  if (!till.isValid) return false;
  return till.diff(now, 'days').days > 0;
};

export const validateQuantity = (quantity: unknown): boolean =>
  typeof quantity === 'number' &&
  Number.isInteger(quantity) &&
  quantity >= 1 &&
  quantity <= MAX_QUANTITY;
